import { Asset, AssetStatus, AssociatedDevice, Masterdata, SortDirection } from '../../components/assets/types';
import { Action, ThunkAction, ThunkDispatch } from '@reduxjs/toolkit';
import { AssetAssociationWithDevice, SelectedTag } from '../../components/assets/details/types';
import { TagCreationFailed, TagCreationSuccessful } from '../tags/types';
import { AssetsListDetailTab } from '../../containers/assets/details/types';
import { State } from '../../../../types';

export const ASSETS_START_LOADING = 'assets/ASSETS_START_LOADING';
export const ASSETS_LOADED = 'assets/ASSETS_LOADED';
export const ASSETS_LOADED_FAILED = 'assets/ASSETS_LOADED_FAILED';
export const ASSOCIATIONS_LOADED_FAILED = 'assets/ASSOCIATIONS_LOADED_FAILED';
export const ASSOCIATIONS_START_LOADING = 'assets/ASSOCIATIONS_START_LOADING';
export const ASSOCIATIONS_LOADED = 'assets/ASSOCIATIONS_LOADED';
export const ASSETS_TABLE_VIEW_TYPE_CHANGED = 'assets/ASSETS_TABLE_VIEW_TYPE_CHANGED';
export const ASSETS_TABLE_ROW_SELECTED = 'assets/ASSETS_TABLE_ROW_SELECTED';
export const ASSETS_TABLE_SORT_CHANGES = 'assets/ASSETS_TABLE_SORT_CHANGES';
export const ASSETS_TABLE_SEARCH_CHANGES = 'assets/ASSETS_TABLE_SEARCH_CHANGES';
export const ASSETS_APPLY_FILTER_CHANGES = 'assets/ASSETS_APPLY_FILTER_CHANGES';
export const ASSETS_START_LOADING_SELECTED_DEVICES = 'assets/ASSETS_START_LOADING_SELECTED_DEVICES';
export const ASSETS_SELECTED_DEVICES_LOADED = 'assets/ASSETS_SELECTED_DEVICES_LOADED';
export const ASSETS_SELECTED_DEVICES_LOADED_FAILED = 'assets/ASSETS_SELECTED_DEVICES_LOADED_FAILED';
export const ASSETS_SELECTED_ASSOCIATED_DEVICES_CHANGED = 'assets/ASSETS_SELECTED_ASSOCIATED_DEVICES_CHANGED';
export const ASSET_UPDATING_STARTED = 'assets/ASSET_UPDATING_STARTED';
export const ASSET_UPDATING_FINISHED = 'assets/ASSET_UPDATING_FINISHED';
export const ASSET_UPDATING_SUCCESSFUL = 'assets/ASSET_UPDATING_SUCCESSFUL';
export const ASSET_UPDATE_RESET_STATE = 'assets/ASSET_UPDATE_RESET_STATE';
export const ASSET_UPDATING_FAILED = 'assets/ASSET_UPDATING_FAILED';
export const SELECTED_ASSET_UPDATE = 'assets/SELECTED_ASSET_UPDATE';
export const SELECTED_MASTERDATA_UPDATE = 'assets/SELECTED_MASTERDATA_UPDATE';
export const ASSET_DELETION_MODAL_OPEN = 'assets/ASSET_DELETION_MODAL_OPEN';
export const ASSET_DELETION_MODAL_CLOSE = 'assets/ASSET_DELETION_MODAL_CLOSE';
export const ASSET_DELETION_STARTED = 'assets/ASSET_DELETION_STARTED';
export const ASSET_DELETION_SUCCESSFUL = 'assets/ASSET_DELETION_SUCCESSFUL';
export const ASSET_DELETION_FAILED = 'assets/ASSET_DELETION_FAILED';
export const ASSET_DELETION_FINISHED = 'assets/ASSET_DELETION_FINISHED';
export const ASSET_DELETION_RESET_STATE = 'assets/ASSET_DELETION_RESET_STATE';
export const IS_ASSETS_RELOAD_IMMINENT = 'assets/IS_ASSETS_RELOAD_IMMINENT';
export const EASY_ONBOARDING_DIALOG_CLOSE = 'assets/EASY_ONBOARDING_DIALOG_CLOSE';
export const EASY_ONBOARDING_DIALOG_OPEN = 'assets/EASY_ONBOARDING_DIALOG_OPEN';
export const ASSET_CLEAR_DEVICE_CACHE = 'assets/ASSET_CLEAR_DEVICE_CACHE';
export const ASSET_TAGS_UPDATING_STARTED = 'assets/ASSET_TAGS_UPDATING_STARTED';
export const ASSET_TAGS_UPDATING_FINISHED = 'assets/ASSET_TAGS_UPDATING_FINISHED';
export const ASSET_TAGS_UPDATING_RESET_STATE = 'assets/ASSET_TAGS_UPDATING_RESET_STATE';
export const ASSET_TAG_REMOVING_SUCCESSFUL = 'assets/ASSET_TAG_REMOVING_SUCCESSFUL';
export const ASSET_TAG_REMOVING_FAILED = 'assets/ASSET_TAG_REMOVING_FAILED';
export const ASSET_TAG_ADDING_SUCCESSFUL = 'assets/ASSET_TAG_ADDING_SUCCESSFUL';
export const ASSET_TAG_ADDING_FAILED = 'assets/ASSET_TAG_ADDING_FAILED';
export const ASSET_TAB_CHANGED = 'assets/ASSET_TAB_CHANGED';
export const FILTER_DIALOG_CLOSE = 'assets/FILTER_DIALOG_CLOSE';
export const FILTER_DIALOG_OPEN = 'assets/FILTER_DIALOG_OPEN';
export const ASSOCIATION_DELETION_STARTED = 'assets/ASSOCIATION_DELETION_STARTED';
export const ASSOCIATION_DELETION_SUCCESSFUL = 'assets/ASSOCIATION_DELETION_SUCCESSFUL';
export const ASSOCIATION_DELETION_FAILED = 'assets/ASSOCIATION_DELETION_FAILED';
export const ASSOCIATION_DELETION_FINISHED = 'assets/ASSOCIATION_DELETION_FINISHED';

export interface AssetsStartLoadingAction extends Action {
    type: typeof ASSETS_START_LOADING;
}

export interface AssetsClearDeviceCacheAction extends Action {
    type: typeof ASSET_CLEAR_DEVICE_CACHE;
    payload: string;
}

export interface AssetsLoadedAction extends Action {
    type: typeof ASSETS_LOADED;
    payload: Asset[];
}

export interface AssetsLoadedFailedAction extends Action {
    type: typeof ASSETS_LOADED_FAILED;
}

export interface AssociationsStartLoadingAction extends Action {
    type: typeof ASSOCIATIONS_START_LOADING;
}

export interface AssociationsLoadedAction extends Action {
    type: typeof ASSOCIATIONS_LOADED;
    payload: AssetAssociationWithDevice[];
}

export interface AssociationsLoadedFailedAction extends Action {
    type: typeof ASSOCIATIONS_LOADED_FAILED;
}

export interface AssetsTableViewTypeChangedAction extends Action {
    type: typeof ASSETS_TABLE_VIEW_TYPE_CHANGED;
    payload: string;
}

export interface AssetTableRowSelectedAction extends Action {
    type: typeof ASSETS_TABLE_ROW_SELECTED;
    payload: Asset | null;
}

export interface AssetTableSortChangesAction extends Action {
    type: typeof ASSETS_TABLE_SORT_CHANGES;
    payload: {
        sortBy: string;
        sortDir: SortDirection;
    };
}

export interface AssetTableSearchChangesAction extends Action {
    type: typeof ASSETS_TABLE_SEARCH_CHANGES;
    payload: {
        searchString: string;
    };
}

export interface AssetsApplyFilterChangesAction extends Action {
    type: typeof ASSETS_APPLY_FILTER_CHANGES;
    payload: {
        showOnlyAssetsWithoutDataSources: boolean;
        showActiveAssets: boolean;
        showArchivedAssets: boolean;
    };
}

export interface AssetsStartLoadingSelectedDevicesAction extends Action {
    type: typeof ASSETS_START_LOADING_SELECTED_DEVICES;
}

export interface AssetsSelectedDevicesLoadedAction extends Action {
    type: typeof ASSETS_SELECTED_DEVICES_LOADED;
    payload: {
        assetId: string;
        associatedDevices: AssociatedDevice[];
    };
}

export interface AssetsSelectedDevicesLoadedFailedAction extends Action {
    type: typeof ASSETS_SELECTED_DEVICES_LOADED_FAILED;
}

export interface AssetsSelectedAssociatedDevicesChangedAction extends Action {
    type: typeof ASSETS_SELECTED_ASSOCIATED_DEVICES_CHANGED;
    payload: {
        selectedAssociatedDevices: AssociatedDevice[];
    };
}

export interface AssetUpdatingStartedAction extends Action {
    type: typeof ASSET_UPDATING_STARTED;
}

export interface AssetUpdatingSuccessfulAction extends Action {
    type: typeof ASSET_UPDATING_SUCCESSFUL;
}

export interface AssetUpdateResetStateAction extends Action {
    type: typeof ASSET_UPDATE_RESET_STATE;
}

export interface AssetUpdatingFailedAction extends Action {
    type: typeof ASSET_UPDATING_FAILED;
    payload: {
        errorCode: string | null;
    };
}

export interface AssetUpdatingFinishedAction extends Action {
    type: typeof ASSET_UPDATING_FINISHED;
}

export interface SelectedMasterdataUpdatedAction extends Action {
    type: typeof SELECTED_MASTERDATA_UPDATE;
    payload: {
        assetId: string;
        masterdata: Masterdata;
    };
}

export interface AssetDeletionModalOpenAction extends Action {
    type: typeof ASSET_DELETION_MODAL_OPEN;
}

export interface AssetDeletionModalCloseAction extends Action {
    type: typeof ASSET_DELETION_MODAL_CLOSE;
}

export interface AssetDeletionStartedAction extends Action {
    type: typeof ASSET_DELETION_STARTED;
}

export interface AssetDeletionSuccessfulAction extends Action {
    type: typeof ASSET_DELETION_SUCCESSFUL;
    payload: {
        assetId: string;
    };
}

export interface AssetDeletionFailedAction extends Action {
    type: typeof ASSET_DELETION_FAILED;
}

export interface AssetDeletionFinishedAction extends Action {
    type: typeof ASSET_DELETION_FINISHED;
}

export interface AssetDeletionResetStateAction extends Action {
    type: typeof ASSET_DELETION_RESET_STATE;
}

export interface SelectedAssetUpdatedAction extends Action {
    type: typeof SELECTED_ASSET_UPDATE;
    payload: {
        assetId: string;
        name: string;
        license_plate: string | null;
        license_plate_country_code: string | null;
        status: AssetStatus;
        brand: string | null;
        identification_type: string | null;
        identification: string | null;
    };
}

export interface IsAssetsReloadImminentAction extends Action {
    type: typeof IS_ASSETS_RELOAD_IMMINENT;
    payload: boolean;
}

export interface EasyOnboardingDialogCloseAction extends Action {
    type: typeof EASY_ONBOARDING_DIALOG_CLOSE;
}

export interface EasyOnboardingDialogOpenAction extends Action {
    type: typeof EASY_ONBOARDING_DIALOG_OPEN;
    payload: {
        selectedAssetId: string | undefined;
    };
}

export interface AssetTagsUpdatingStartedAction extends Action {
    type: typeof ASSET_TAGS_UPDATING_STARTED;
}

export interface AssetTagRemovingSuccessfulAction extends Action {
    type: typeof ASSET_TAG_REMOVING_SUCCESSFUL;
    payload: {
        tag: SelectedTag;
        asset: Asset;
    };
}

export interface AssetTagRemovingFailedAction extends Action {
    type: typeof ASSET_TAG_REMOVING_FAILED;
    payload: {
        tag: SelectedTag;
        asset: Asset;
    };
}

export interface AssetTagAddingSuccessfulAction extends Action {
    type: typeof ASSET_TAG_ADDING_SUCCESSFUL;
    payload: {
        tag: SelectedTag;
        asset: Asset;
    };
}

export interface AssetTagAddingFailedAction extends Action {
    type: typeof ASSET_TAG_ADDING_FAILED;
    payload: {
        tag: SelectedTag;
        asset: Asset;
    };
}

export interface AssetTagsUpdatingFinishedAction extends Action {
    type: typeof ASSET_TAGS_UPDATING_FINISHED;
}

export interface AssetTagsUpdatingResetStateAction extends Action {
    type: typeof ASSET_TAGS_UPDATING_RESET_STATE;
}

export interface AssetsTabChangedAction extends Action {
    type: typeof ASSET_TAB_CHANGED;
    payload: {
        selectedTab: AssetsListDetailTab;
    };
}

export interface FilterDialogCloseAction extends Action {
    type: typeof FILTER_DIALOG_CLOSE;
}

export interface FilterDialogOpenAction extends Action {
    type: typeof FILTER_DIALOG_OPEN;
}

export interface AssociationDeletionStartedAction extends Action {
    type: typeof ASSOCIATION_DELETION_STARTED;
}

export interface AssociationDeletionSuccessfulAction extends Action {
    type: typeof ASSOCIATION_DELETION_SUCCESSFUL;
    payload: {
        associationId: string;
    };
}

export interface AssociationDeletionFailedAction extends Action {
    type: typeof ASSOCIATION_DELETION_FAILED;
}

export interface AssociationDeletionFinishedAction extends Action {
    type: typeof ASSOCIATION_DELETION_FINISHED;
}

export type AssetsActions =
    | EasyOnboardingDialogOpenAction
    | EasyOnboardingDialogCloseAction
    | IsAssetsReloadImminentAction
    | AssetDeletionFailedAction
    | AssetDeletionFinishedAction
    | AssetDeletionResetStateAction
    | AssetDeletionModalCloseAction
    | AssetDeletionModalOpenAction
    | AssetDeletionStartedAction
    | AssetDeletionSuccessfulAction
    | AssetsApplyFilterChangesAction
    | AssetsClearDeviceCacheAction
    | AssetsLoadedAction
    | AssetsLoadedFailedAction
    | AssetsSelectedAssociatedDevicesChangedAction
    | AssetsSelectedDevicesLoadedAction
    | AssetsSelectedDevicesLoadedFailedAction
    | AssetsStartLoadingAction
    | AssetsStartLoadingSelectedDevicesAction
    | AssetsTabChangedAction
    | AssetsTableViewTypeChangedAction
    | AssetTableRowSelectedAction
    | AssetTableSearchChangesAction
    | AssetTableSortChangesAction
    | AssetTagAddingFailedAction
    | AssetTagAddingSuccessfulAction
    | AssetTagRemovingFailedAction
    | AssetTagRemovingSuccessfulAction
    | AssetTagsUpdatingFinishedAction
    | AssetTagsUpdatingResetStateAction
    | AssetTagsUpdatingStartedAction
    | AssetUpdatingFailedAction
    | AssetUpdatingFinishedAction
    | AssetUpdatingStartedAction
    | AssetUpdatingSuccessfulAction
    | AssetUpdateResetStateAction
    | AssociationDeletionFailedAction
    | AssociationDeletionFinishedAction
    | AssociationDeletionStartedAction
    | AssociationDeletionSuccessfulAction
    | AssociationsLoadedAction
    | AssociationsLoadedFailedAction
    | AssociationsStartLoadingAction
    | FilterDialogCloseAction
    | FilterDialogOpenAction
    | SelectedAssetUpdatedAction
    | SelectedMasterdataUpdatedAction
    | TagCreationFailed
    | TagCreationSuccessful;

export type AssetThunkAction<R> = ThunkAction<R, State, undefined, AssetsActions>;

export type AssetsThunkDispatch = ThunkDispatch<State, undefined, AssetsActions>;
